import mapValues from 'lodash/mapValues'
import partition from 'lodash/partition'

import { AbortError } from '@app/errors/AbortError'

import { createSelector } from '@app/utils/createSelector'
import { isTruthy } from '@app/utils/isTruthy'
import { createLocalStorage } from '@app/utils/localStorage'
import { normalize } from '@app/utils/normalizer'
import { omit } from '@app/utils/omit'
import { retry } from '@app/utils/retry'
import { sleep } from '@app/utils/sleep'
import { tryJSONparse } from '@app/utils/tryJSONparse'

import { asApiError } from '@app/packages/api/errors'
import type { AcademyInvoiceCreateRequest } from '@app/packages/api/v2/types/params'
import { intoResultAsync, resultError, resultOk, unwrapOr } from '@app/packages/Result/Result'
import { createLoadable, resolveThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { getAbortControllerService } from '@app/store/actions/contexts/abortControllerService'
import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserSelector } from '@app/store/slices/profile'
import { shortSessionUuidSelector } from '@app/store/slices/short_session'
import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import {
  cartInvoiceMetaSelector,
  cartInvoiceSelector,
  promocodeSelector,
  purchaseLessons,
  setAcademyInvoice,
  setAcademyInvoiceMeta,
  setAcademyPromocode,
} from './invoice'
import { addLessonModels } from './lessons'
import type { AcademyLesson } from './lib'
import { toCourseBundle } from './lib'
import { academyUserResultSelector, academyUserSelector } from './profile'

export type AcademyCart = {
  items: (AcademyLesson & { not_available_reason: string | null })[]
  kidName: string
  kidAge: number
  name: string
  phone: string
  email: string
  promocode: string
}

const academyCartSlice = createReduxSlice<SliceState<AcademyCart>>('academy_cart')

// Merges cart fields into the (restored) cart slice; cart items also flow into the shared lesson
// models map so the rest of the app can read them by id.
export function setAcademyCart(payload: Partial<AcademyCart>) {
  return createThunk(dispatch => {
    dispatch(academyCartSlice.update(state => (state ? { ...state, data: { ...state.data, ...payload } } : state)))
    if (payload.items) dispatch(addLessonModels(payload.items.map(item => omit(item, 'not_available_reason'))))
  })
}

// Hash is the IS_BROWSER flag: the server returns an empty cart without loading, while the
// client (different hash) restores from localStorage on hydration. Components suspend on this.
export const cartResultSelector = createSelector([academyCartSlice.selector], sliceState =>
  createLoadable(c => {
    if (!IS_BROWSER) return EMPTY_CART
    return withCheckedSliceState(c, {
      sliceState,
      slice: academyCartSlice,
      hash: String(IS_BROWSER),
      load: dispatch => dispatch(restoreCart()),
    })
  })
)

export const cartSelector = createSelector([academyCartSlice.selector], slice => slice?.data ?? EMPTY_CART)

export const cartValidItemsSelector = createSelector([cartSelector], cart => cart.items.filter(i => !i.not_available_reason))

export const cartValidInvalidPartitionItemsSelector = createSelector([cartSelector], cart => partition(cart.items, i => !i.not_available_reason))

export const cartValidItemsCountSelector = createSelector([cartValidItemsSelector], items => items.length)

export const CART_REQUEST_LABEL = 'academy-cart'

/**
 * Refreshes the invoice for the current cart. Resolves to a `Result<void>`: ok on success,
 * an error result wrapping the failure (or an `AbortError` when the refresh is skipped/aborted
 * because another cart request is in flight). Callers may ignore the result.
 *
 * @param silent should action dispatch cartInvoiceMeta.loading change
 */
export function refreshCart(silent = true) {
  return createThunk((dispatch, getState) =>
    intoResultAsync(
      (async (): Promise<void> => {
        const abort = dispatch(getAbortControllerService())
        const state = getState()
        const meta = cartInvoiceMetaSelector(state)
        if (meta.loading || meta.purchasing || meta.prePurchasing) throw new AbortError('Cart refresh skipped: another cart request is in flight')
        const abortController = abort.createAbortController(CART_REQUEST_LABEL)

        if (!silent) dispatch(setAcademyInvoiceMeta({ loading: true }))

        const cart = cartSelector(state)
        const response = await dispatch(
          fetchAcademyCart(
            cart.items.map(i => i.id),
            cart.email,
            cart.phone,
            cart.promocode
          )
        )

        if (!silent && !abortController.signal.aborted) dispatch(setAcademyInvoiceMeta({ loading: false }))

        if (response.error) throw response.value

        const newMeta = cartInvoiceMetaSelector(getState())

        if (abortController.signal.aborted || newMeta.loading || newMeta.purchasing) throw new AbortError('Cart refresh aborted')

        dispatch(setAcademyCart({ items: response.value.items }))
        dispatch(setAcademyInvoice(resultOk(response.value.cart)))
        dispatch(setAcademyPromocode(response.value.promocode ? resultOk(response.value.promocode) : null))
      })()
    )
  )
}

export function fetchAcademyCart(lesson_ids: string[], email: string, phone: string, promocode: string) {
  return createThunk((dispatch, getState) =>
    intoResultAsync(
      (async () => {
        const [{ postAcademyCarts }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/academy'), import('@app/store/actions/api')])
        const session_id = shortSessionUuidSelector(getState())

        const payload = await dispatch(
          callV2Api(
            postAcademyCarts({
              session_id,
              items: lesson_ids.map(lesson_id => ({ lesson_id })),
              email,
              phone,
              promocode: promocode.trim(),
            })
          )
        )

        const {
          academy_cart_items = {},
          academy_courses = {},
          academy_lessons = {},
          avatars = {},
          users = {},
          academy_lesson_pictures = {},
        } = normalize(payload)
        const cartItemIds = payload.data.relationships.items.data!.map(item => item.id)
        const cartItems = cartItemIds.map(id => {
          const cartItem = academy_cart_items[id]
          const lesson = academy_lessons[cartItem.relationships.lesson.data!.id]
          return { ...cartItem, lesson }
        })

        const courses = mapValues(academy_courses, toCourseBundle)
        const storeAvatars = avatars ? mapValues(avatars, a => ({ id: a.id, ...a.attributes })) : {}
        const storeUsers = users
          ? mapValues(users, user => {
              const sitter = user
              const avatars = sitter.relationships.avatars?.data ? sitter.relationships.avatars.data.map(a => storeAvatars[a.id]) : []
              return { id: sitter.id, ...sitter.attributes, avatars }
            })
          : {}

        const items: (AcademyLesson & { not_available_reason: string | null })[] = cartItems.map(cartItem => ({
          id: cartItem.lesson.id,
          ...cartItem.lesson.attributes,
          pictures: (cartItem.lesson.relationships.pictures?.data ?? []).map(p => academy_lesson_pictures[p.id]).filter(isTruthy),
          course: courses[cartItem.lesson.relationships.course.data!.id],
          sitter: cartItem.lesson.relationships.sitter.data ? storeUsers[cartItem.lesson.relationships.sitter.data.id] : null,
          not_available_reason: cartItem.attributes.not_available_reason,
        }))

        return {
          cart: payload.data,
          promocode: payload.included?.find(t => t.type === 'promocodes') || null,
          items,
        }
      })()
    )
  )
}

export function resetCartStoreItems() {
  return createThunk(() => {
    cartStore.set({ ...cartStore.get(), lesson_ids: [] })
  })
}

export function addToCart(lesson: AcademyLesson) {
  return fetchCartWrapper((dispatch, getState) => {
    const items = cartSelector(getState()).items
    const index = items.findIndex(i => i.id === lesson.id)
    if (index === -1) {
      dispatch(setAcademyCart({ items: [...items, { ...lesson, not_available_reason: null }] }))
      saveCart(cartSelector(getState()))
    }
  })
}

export function removeFromCart(...lessons: AcademyLesson[]) {
  return fetchCartWrapper((dispatch, getState) => {
    const items = cartSelector(getState()).items
    const ids = lessons.map(l => l.id)
    const newItems = [...items].filter(l => !ids.includes(l.id))

    if (newItems.length === items.length) return

    dispatch(setAcademyCart({ items: newItems }))
    saveCart(cartSelector(getState()))
  })
}

export function clearCart() {
  return fetchCartWrapper((dispatch, getState) => {
    dispatch(setAcademyCart({ items: [] }))
    saveCart(cartSelector(getState()))
  })
}

export function editCartForm(form: Omit<Partial<AcademyCart>, 'items'>) {
  return fetchCartWrapper((dispatch, getState) => {
    dispatch(setAcademyCart(form))
    saveCart(cartSelector(getState()))
  })
}

export function purchaseCart() {
  return createThunk<
    Promise<
      | { type: 'success'; data: AcademyInvoiceCreateRequest }
      | { type: 'cancel' }
      | { type: 'error'; stage: 'invoice' | 'purchase' | 'cloudpayments'; data: Error }
    >
  >(async (dispatch, getState) => {
    const { analyticsEvent } = await import('@app/packages/analytics/declaration')
    const { getAnalyticsRouter } = await import('@app/store/actions/analytics')
    const cart = cartSelector(getState())
    const invoice = cartInvoiceSelector(getState())
    const promocode = promocodeSelector(getState())
    if (cartInvoiceMetaSelector(getState()).loading || !invoice || invoice.error) return { type: 'cancel' }

    dispatch(setAcademyInvoiceMeta({ purchasing: true, purchaseError: null }))

    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // Started paying for an academy cart. Payment still follows.
        type: 'academy_cart_purchase_start',
      })).set('google', 'yandex', 'amplitude', 'sentry')
    )

    const result = await dispatch(
      purchaseLessons(
        {
          name: cart.name,
          kidAge: cart.kidAge,
          kidName: cart.kidName,
          email: cart.email,
          phone: cart.phone,
          lesson_ids: cart.items.filter(l => !l.not_available_reason).map(l => l.id),
          promocode: promocode ? (promocode.error ? undefined : promocode.value.attributes.token) : undefined,
        },
        { withProgress: true }
      )
    )

    if (result.type === 'cancel') {
      dispatch(setAcademyInvoiceMeta({ purchasing: false }))
      return result
    }

    if (result.type === 'error') {
      void dispatch(refreshCart())

      dispatch(setAcademyInvoiceMeta({ purchasing: false, purchaseError: result.data }))
      return result
    }

    const ids = result.data.items.map(i => i.lesson_id)
    dispatch(getAnalyticsRouter()).send(
      analyticsEvent(() => ({
        // An academy cart was paid for.
        type: 'academy_cart_purchase_success',
        data: { lesson_ids: ids, lessons_count: ids.length, promocode: result.data.promocode, payment_method: result.paymentMethod },
      })).set('amplitude', 'sentry', 'yandex', 'google')
    )

    dispatch(setAcademyInvoiceMeta({ purchasing: false, purchaseError: null }))
    return result
  })
}

const EMPTY_CART: AcademyCart = { items: [], kidName: '', kidAge: 0, name: '', phone: '', email: '', promocode: '' }

/** academy cart Localstorage state */
type CartLSState = {
  name: string
  phone: string
  email: string
  kidName: string
  kidAge: number
  lesson_ids: string[]
}

const defaultCartLSState: CartLSState = {
  name: '',
  phone: '',
  email: '',
  kidName: '',
  kidAge: 0,
  lesson_ids: [],
}

const cartStore = createLocalStorage<CartLSState>(
  'kidsout__academy-cart',
  value => ({ ...defaultCartLSState, ...unwrapOr(tryJSONparse<any>(value), {}) }),
  value => JSON.stringify(value)
)

const saveCart = (cart: AcademyCart) => {
  const ids = cart.items.map(l => l.id)
  const newState: CartLSState = { ...omit(cart, 'items', 'promocode'), lesson_ids: ids }
  cartStore.set(newState)
}

// wraps action so if it changes cart items or promocode it fetches invoice for that cart
function fetchCartWrapper<T>(fn: ThunkAction<T>) {
  return createThunk((dispatch, getState) => {
    const abort = dispatch(getAbortControllerService())
    const prevCart = cartSelector(getState())

    const runner = async () => {
      const newCart = cartSelector(getState())
      const statesEqual =
        prevCart.email === newCart.email && prevCart.phone === newCart.phone && prevCart.promocode === newCart.promocode && prevCart.items === newCart.items
      if (statesEqual) return

      const abortController = abort.createAbortController(CART_REQUEST_LABEL)

      if (newCart.items.length === 0) {
        dispatch(setAcademyInvoice(null))
        return
      }

      dispatch(setAcademyInvoiceMeta({ loading: true }))

      let shouldSleep = true
      let promocodeError: Error | null = null

      while (true) {
        if (shouldSleep) {
          await sleep(500)
        } else {
          shouldSleep = true
        }

        if (abortController.signal.aborted) return

        const response = await dispatch(
          fetchAcademyCart(
            newCart.items.map(l => l.id),
            promocodeError ? '' : newCart.email,
            promocodeError ? '' : newCart.phone,
            promocodeError ? '' : newCart.promocode
          )
        )

        if (abortController.signal.aborted) return

        if (response?.error) {
          const isPromocodeError = !!asApiError(response.value)?.response?.errors.find(e => e.meta.email || e.meta.phone || e.meta.promocode)

          // if error was about promocode then we exit loop
          // if not we sleep for 500 + 500 (on loop start) and retry
          if (isPromocodeError) {
            promocodeError = response.value as Error
            shouldSleep = false
          } else {
            await sleep(500)
          }
        } else {
          dispatch(setAcademyCart({ items: response.value.items }))
          dispatch(setAcademyInvoice(resultOk(response.value.cart)))
          dispatch(setAcademyPromocode(promocodeError ? resultError(promocodeError) : response.value.promocode ? resultOk(response.value.promocode) : null))
          break
        }
      }

      dispatch(setAcademyInvoiceMeta({ loading: false }))
    }

    const result = dispatch(fn)
    dispatch(setAcademyInvoiceMeta({ purchaseError: null }))
    if (result instanceof Promise) {
      void result.then(() => runner())
    } else {
      void runner()
    }
    return result
  })
}

// Restores the cart from localStorage (and fetches its invoice items). Internal — callers
// reach it only by suspending cartResultSelector.
function restoreCart() {
  return createThunk(async (dispatch, getState): Promise<AcademyCart> => {
    await dispatch(resolveThunkSelector(academyUserResultSelector))

    const state = getState()
    const academyUser = academyUserSelector(state)
    const localState = cartStore.get()
    const profileUser = profileUserSelector(state)
    if (!localState.name && profileUser && 'name' in profileUser) {
      localState.name = [profileUser.first_name, profileUser.last_name].join(' ')
    }

    if (!localState.email && academyUser?.email) {
      localState.email = academyUser.email
    } else if (!localState.email && profileUser && 'email' in profileUser && profileUser.email) {
      localState.email = profileUser.email
    }

    if (!localState.phone && profileUser && 'phone' in profileUser && profileUser.phone) {
      localState.phone = profileUser.phone
    }

    const cart: AcademyCart = { items: [], promocode: '', ...omit(localState, 'lesson_ids') }

    if (!localState.lesson_ids.length) {
      dispatch(setAcademyInvoiceMeta({ loading: false }))
      return cart
    }

    dispatch(setAcademyInvoiceMeta({ loading: true }))

    const payload = await retry(
      () =>
        dispatch(fetchAcademyCart(localState.lesson_ids, '', '', '')).then(resp => {
          if (resp.error) throw resp.value
          return resp.value
        }),
      { attempts: 3, interval: 500 }
    )

    cart.items = payload.items

    dispatch(setAcademyInvoice(resultOk(payload.cart)))
    dispatch(setAcademyPromocode(payload.promocode ? resultOk(payload.promocode) : null))
    dispatch(setAcademyInvoiceMeta({ loading: false }))

    return cart
  })
}
